// Color system for Donde Bailar.
// Psychology: coral invites action, amber signals celebration/progress,
// teal provides trust/orientation, and wine-black keeps the night/social mood.

export const colors = {
  primary: {
    50: '#FFF3EF',
    100: '#FFE0D7',
    200: '#FFC1B0',
    300: '#FF987F',
    400: '#FF7054',
    500: '#F25A3C',
    600: '#D9432B',
    700: '#B83324',
    800: '#8F2A22',
    900: '#5B1E1A',
  },

  secondary: {
    50: '#FFF9EA',
    100: '#FFF0C5',
    200: '#FFE18A',
    300: '#FFCC4A',
    400: '#F6B323',
    500: '#D99A16',
    600: '#B87910',
    700: '#925C10',
    800: '#704611',
    900: '#4F3210',
  },

  deep: {
    50: '#F7F2F4',
    100: '#EBDDE2',
    200: '#D6BBC4',
    300: '#B98D9A',
    400: '#8E6170',
    500: '#5B3442',
    600: '#432533',
    700: '#2F1B27',
    800: '#1F141E',
    900: '#130D14',
  },

  accent: {
    50: '#ECFEFF',
    100: '#CFFAFE',
    200: '#A5F3FC',
    300: '#67E8F9',
    400: '#22D3EE',
    500: '#18A8B8',
    600: '#128494',
    700: '#116C79',
    800: '#155865',
    900: '#164956',
  },

  gray: {
    50: '#FAF7F5',
    100: '#F2ECE8',
    200: '#E2D9D3',
    300: '#C9BCB4',
    400: '#A99790',
    500: '#8C7B75',
    600: '#6F615D',
    700: '#564B49',
    800: '#3B3334',
    900: '#211C20',
  },

  dark: {
    50: '#24171D',
    100: '#21151B',
    200: '#1E1319',
    300: '#1A1117',
    400: '#160F14',
    500: '#120C11',
    600: '#0F0A0E',
    700: '#0B070A',
    800: '#070506',
    900: '#000000',
  },

  success: '#10B981',
  warning: '#D99A16',
  error: '#EF4444',
  info: '#18A8B8',

  glass: {
    light: 'rgba(255, 255, 255, 0.1)',
    medium: 'rgba(255, 255, 255, 0.18)',
    strong: 'rgba(255, 255, 255, 0.28)',
    dark: 'rgba(19, 13, 20, 0.24)',
    darker: 'rgba(19, 13, 20, 0.42)',
  },

  gradients: {
    primary: 'linear-gradient(135deg, #F25A3C 0%, #D99A16 100%)',
    secondary: 'linear-gradient(135deg, #D99A16 0%, #18A8B8 100%)',
    deep: 'linear-gradient(135deg, #130D14 0%, #2F1B27 100%)',
    glass: 'linear-gradient(135deg, rgba(255, 255, 255, 0.11) 0%, rgba(255, 255, 255, 0.045) 100%)',
    dark: 'linear-gradient(135deg, #160F14 0%, #24171D 100%)',
    warm: 'linear-gradient(135deg, #F25A3C 0%, #D99A16 54%, #18A8B8 100%)',
  },

  shadows: {
    sm: '0 2px 4px rgba(0, 0, 0, 0.12)',
    md: '0 4px 8px rgba(0, 0, 0, 0.16)',
    lg: '0 8px 16px rgba(0, 0, 0, 0.22)',
    xl: '0 16px 32px rgba(0, 0, 0, 0.28)',
    glow: '0 0 20px rgba(242, 90, 60, 0.32)',
    glass: '0 8px 32px rgba(0, 0, 0, 0.32)',
  },

  light: '#FFFFFF',
  darkBase: '#160F14',
  blue: '#18A8B8',
  coral: '#F25A3C',
  orange: '#D99A16',
  yellow: '#F6C65B',
};

export const typography = {
  fontFamily: {
    primary: '"Poppins", "Inter", system-ui, sans-serif',
    secondary: '"Inter", system-ui, sans-serif',
  },
  fontSize: {
    xs: '0.75rem',
    sm: '0.875rem',
    base: '1rem',
    lg: '1.125rem',
    xl: '1.25rem',
    '2xl': '1.5rem',
    '3xl': '1.875rem',
    '4xl': '2.25rem',
    '5xl': '3rem',
  },
  fontWeight: {
    light: '300',
    normal: '400',
    medium: '500',
    semibold: '600',
    bold: '700',
    extrabold: '800',
    black: '900',
  },
  lineHeight: {
    tight: '1.25',
    normal: '1.5',
    relaxed: '1.75',
  },
};

export const spacing = {
  0: '0',
  1: '0.25rem',
  2: '0.5rem',
  3: '0.75rem',
  4: '1rem',
  5: '1.25rem',
  6: '1.5rem',
  8: '2rem',
  10: '2.5rem',
  12: '3rem',
  16: '4rem',
  20: '5rem',
  24: '6rem',
};

export const borderRadius = {
  none: '0',
  sm: '0.25rem',
  md: '0.5rem',
  lg: '0.75rem',
  xl: '1rem',
  '2xl': '1.5rem',
  '3xl': '2rem',
  full: '9999px',
};

export const transitions = {
  fast: '0.15s ease-in-out',
  normal: '0.3s ease-in-out',
  slow: '0.5s ease-in-out',
  bounce: '0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
};

export default {
  colors,
  typography,
  spacing,
  borderRadius,
  transitions,
};

export const theme = {
  bg: {
    app: `linear-gradient(135deg, ${colors.dark[400]} 0%, ${colors.dark[50]} 100%)`,
    card: colors.glass.light,
    surface: colors.glass.medium,
  },
  text: {
    primary: colors.light,
    secondary: colors.gray[200],
    medium: colors.gray[300],
    muted: colors.gray[400],
  },
  brand: {
    primary: colors.gradients.primary,
    secondary: colors.gradients.secondary,
  },
  palette: {
    gray3: colors.glass.medium,
    blue: colors.blue,
    coral: colors.coral,
    orange: colors.orange,
  },
  spacing: (value: number) => spacing[value as keyof typeof spacing] || `${value * 0.25}rem`,
  radius: {
    sm: borderRadius.sm,
    md: borderRadius.md,
    lg: borderRadius.lg,
    xl: borderRadius['2xl'],
  },
};

export type Theme = typeof theme;
